import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import NFTCard from "@/components/NFTCard";
import { ShieldCheck, GraduationCap, Briefcase, Lock } from "lucide-react";

const STUDENT_AVATAR = "https://static.prod-images.emergentagent.com/jobs/3314a7a0-c2a5-4cde-8293-8d2f5e2e9aac/images/454c6b6a208814c20bc43411a879692f5a3bcbd042c8946b51b35f42b783b8fd.png";
const TEACHER_AVATAR = "https://static.prod-images.emergentagent.com/jobs/3314a7a0-c2a5-4cde-8293-8d2f5e2e9aac/images/fef536d6c8297707daeb42f9e8e2b28ba74369a520e9b8fb44fd9f8ab1808074.png";

export default function Profile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    api.get(`/users/${userId}`).then(({ data }) => setProfile(data));
  }, [userId]);

  useEffect(() => {
    if (user && user.id === userId) {
      api.get("/assets/mine", { params: { tab: "files" } }).then(({ data }) => setFiles(data));
    }
  }, [user, userId]);

  if (!profile) return <div className="py-20 grid place-items-center text-zinc-500">Loading…</div>;
  const isSelf = user && user.id === userId;
  const avatar = profile.role === "teacher" ? TEACHER_AVATAR : STUDENT_AVATAR;

  // Private profile guard
  if (!profile.public_profile && !isSelf) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <Lock className="mx-auto text-zinc-500" size={36} />
        <h1 className="font-display text-3xl mt-4">Private profile</h1>
        <p className="text-zinc-500 mt-2">{profile.name} has set this profile to private.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      {/* Header */}
      <div className="flex items-end gap-6 flex-wrap">
        <img src={profile.avatar_url || avatar} alt="" className="w-24 h-24 rounded-xl border border-white/10 object-cover" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
            {profile.role === "teacher" ? <Briefcase size={12} /> : <GraduationCap size={12} />}
            {profile.role}
          </div>
          <h1 className="font-display text-4xl tracking-tight mt-1">{profile.name}</h1>
          <div className="text-sm text-zinc-500 mt-1">{profile.school_name || "—"}</div>
        </div>
        {profile.role === "teacher" && profile.teacher_verified && (
          <div className="flex items-center gap-2 text-cyan8r text-sm border border-cyan8r/30 bg-cyan8r/5 px-3 py-1.5 rounded-md">
            <ShieldCheck size={14} /> Verified Teacher
          </div>
        )}
      </div>

      {profile.bio && <p className="mt-5 text-zinc-400 max-w-2xl">{profile.bio}</p>}

      {/* Tabs */}
      <div className="mt-10">
        <Tabs defaultValue="catalog">
          <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start gap-6">
            <TabsTrigger value="catalog" data-testid="profile-tab-catalog" className="data-[state=active]:text-white text-zinc-500 data-[state=active]:border-b-2 data-[state=active]:border-cyan8r rounded-none pb-3">
              Catalog ({profile.catalog?.length || 0})
            </TabsTrigger>
            {isSelf && (
              <TabsTrigger value="files" data-testid="profile-tab-files" className="data-[state=active]:text-white text-zinc-500 data-[state=active]:border-b-2 data-[state=active]:border-cyan8r rounded-none pb-3">
                Files ({files.length})
              </TabsTrigger>
            )}
            {profile.role === "teacher" && (
              <TabsTrigger value="classes" data-testid="profile-tab-classes" className="data-[state=active]:text-white text-zinc-500 data-[state=active]:border-b-2 data-[state=active]:border-cyan8r rounded-none pb-3">
                Classes ({profile.classes?.length || 0})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="catalog" className="mt-6">
            {(profile.catalog || []).length === 0 ? (
              <div className="border border-dashed border-white/10 rounded-xl p-10 text-center text-zinc-500">
                No public assets yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {profile.catalog.map((a) => <NFTCard key={a.id} asset={a} />)}
              </div>
            )}
          </TabsContent>

          {isSelf && (
            <TabsContent value="files" className="mt-6">
              {files.length === 0 ? (
                <div className="border border-dashed border-white/10 rounded-xl p-10 text-center text-zinc-500">
                  Nothing in your files yet — mint your first asset.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {files.map((a) => <NFTCard key={a.id} asset={a} />)}
                </div>
              )}
            </TabsContent>
          )}

          {profile.role === "teacher" && (
            <TabsContent value="classes" className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(profile.classes || []).map((c) => (
                <div key={c.id} className="card-surface p-5">
                  <div className="font-display text-lg">{c.name}</div>
                  <div className="text-xs text-zinc-500 mt-1">{c.student_ids?.length || 0} students</div>
                  {c.description && <div className="text-sm text-zinc-400 mt-2">{c.description}</div>}
                </div>
              ))}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
